import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lightbulb, Users, BookOpen, Award, FlaskConical, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Layout from '../components/layout/Layout';
import { images } from '../data/mock';

const Research = () => {
  const researchAreas = [
    {
      icon: FlaskConical,
      title: 'Applied Research',
      description: 'Conducting practical research that addresses real-world challenges in healthcare, technology, business, and tourism.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation Hub',
      description: 'Fostering creative solutions and entrepreneurial thinking among students and faculty members.',
    },
    {
      icon: Users,
      title: 'Community Engagement',
      description: 'Partnering with local communities to identify needs and develop sustainable solutions.',
    },
    {
      icon: Handshake,
      title: 'Industry Partnerships',
      description: 'Collaborating with industry leaders to ensure research outcomes meet market demands.',
    },
  ];

  const initiatives = [
    {
      title: 'Digital Innovation Lab',
      description: 'A dedicated space for students to develop AI, cybersecurity, and software solutions.',
      image: images.techLab,
    },
    {
      title: 'Healthcare Research Unit',
      description: 'Supporting research in pharmaceutical sciences and clinical practices.',
      image: images.medical,
    },
    {
      title: 'Sustainable Tourism Project',
      description: 'Researching eco-friendly tourism practices for Tanzania\'s growing industry.',
      image: images.hero,
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${images.scienceLab})` }}
        >
          <div className="absolute inset-0 overlay-navy" />
        </div>
        <div className="container-custom relative z-10">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
            Research & Innovation
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Advancing knowledge and driving sustainable progress through applied research and innovation.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-6">
                Driving Innovation for a Better Future
              </h2>
              <p className="text-[#475569] leading-relaxed mb-6">
                At Baobab Institute of Tanzania, we believe that research and innovation are essential to addressing the challenges facing our society. Our mission includes advancing applied research that creates real impact.
              </p>
              <p className="text-[#475569] leading-relaxed mb-6">
                We engage with local and global communities, building strong institutional partnerships that foster innovation, entrepreneurship, and social responsibility across all sectors.
              </p>
              <p className="text-[#475569] leading-relaxed">
                Our focus on securing the digital future drives our commitment to developing cutting-edge solutions in AI, cybersecurity, and digital transformation.
              </p>
            </div>
            <div className="relative">
              <img
                src={images.techLab}
                alt="Research at BIT"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Research Areas */}
      <section className="section-padding bg-[#f8fafc]">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Our Focus Areas
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Key areas where we drive research and innovation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {researchAreas.map((area, index) => (
              <Card key={index} className="border-none shadow-md card-hover text-center">
                <CardContent className="pt-8">
                  <div className="w-16 h-16 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <area.icon className="w-8 h-8 text-[#d4a855]" />
                  </div>
                  <h3 className="font-display font-semibold text-[#1e3a5f] text-lg mb-2">
                    {area.title}
                  </h3>
                  <p className="text-[#475569] text-sm">
                    {area.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Research Initiatives */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Current Initiatives
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Ongoing projects and research initiatives at BIT.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {initiatives.map((initiative, index) => (
              <Card key={index} className="overflow-hidden border-none shadow-lg card-hover">
                <div className="relative h-48">
                  <img
                    src={initiative.image}
                    alt={initiative.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f] to-transparent" />
                </div>
                <CardContent className="pt-4">
                  <h3 className="font-display font-semibold text-[#1e3a5f] text-xl mb-2">
                    {initiative.title}
                  </h3>
                  <p className="text-[#475569] text-sm">
                    {initiative.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Student Involvement */}
      <section className="py-16 bg-[#1e3a5f]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
                Student Research Opportunities
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                We encourage students to participate in research projects as part of their academic journey. From final year projects to collaborative research with faculty, students gain hands-on experience in applied research.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Capstone research projects',
                  'Industry attachment research',
                  'Student innovation competitions',
                  'Faculty-mentored research',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/90">
                    <Award className="w-5 h-5 text-[#d4a855]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/programmes">
                <Button className="bg-[#d4a855] hover:bg-[#f4d794] text-[#142942] font-semibold">
                  Explore Programmes
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src={images.students}
                alt="Students collaborating"
                className="w-full h-[350px] object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container-custom text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
            Partner With Us
          </h2>
          <p className="text-[#475569] max-w-xl mx-auto mb-8">
            Interested in collaboration? We welcome partnerships with industry, government, and academic institutions.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#1e3a5f] hover:bg-[#2563eb] text-white font-semibold px-8">
              Contact Us
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default Research;
